/**
 * Os arquivos presos à tarefa — frente do roadmap ou card do Dev Board.
 *
 * <p>Já dava para anexar num <b>comentário</b>, e não é a mesma coisa: comentário é
 * conversa, e afunda assim que a discussão anda. O contrato assinado, o print do bug e a
 * planilha de custos não são fala de ninguém — são o material da tarefa, e quem abre o
 * card no mês seguinte precisa achá-los sem rolar trinta comentários.
 *
 * <p>Dois passos no envio, e não um: o arquivo sobe em {@code POST /api/anexos} e só
 * depois a referência é presa à tarefa. Um multipart único com cinco arquivos falharia
 * inteiro quando o quarto falhasse — e o quarto falhar é o caso comum.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { abrirAnexo } from '../comum/abrirAnexo';
import { ImagemAutenticada } from '../comum/ImagemAutenticada';
import { tamanhoLegivel as tamanho } from '../comum/tamanhoLegivel';
import { subirAnexo } from './comentariosApi';
import './anexos.css';

export interface AnexoDaTarefa {
  id: number;
  url: string;
  miniatura: string | null;
  nomeOriginal: string;
  bytes: number;
  anexadoPor: string | null;
  anexadoEm: string | null;
}

export const chaveAnexos = (tarefaId: number) => ['anexos', tarefaId] as const;

/**
 * Quantos anexos a tarefa tem — para o número da aba.
 *
 * <p>Mesma chave da lista, então o React Query serve as duas com uma requisição só: abrir
 * a aba não refaz a consulta que o contador já fez.
 */
export function useQuantosAnexos(tarefaId: number): number {
  const { data } = useQuery<AnexoDaTarefa[]>({
    queryKey: chaveAnexos(tarefaId),
    queryFn: () => api(`/api/tarefas/${tarefaId}/anexos`),
  });
  return data?.length ?? 0;
}

export function AnexosDaTarefa({ tarefaId }: { tarefaId: number }) {
  const fila = useQueryClient();
  const campo = useRef<HTMLInputElement>(null);
  const [subindo, setSubindo] = useState(0);
  const [erro, setErro] = useState<string | null>(null);

  const { data, isPending } = useQuery<AnexoDaTarefa[]>({
    queryKey: chaveAnexos(tarefaId),
    queryFn: () => api(`/api/tarefas/${tarefaId}/anexos`),
  });

  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: chaveAnexos(tarefaId) });
  };

  const prender = useMutation({
    mutationFn: (corpo: {
      url: string;
      miniatura: string | null;
      nomeOriginal: string;
      bytes: number;
    }) =>
      api<AnexoDaTarefa>(`/api/tarefas/${tarefaId}/anexos`, {
        method: 'POST',
        body: JSON.stringify(corpo),
      }),
    onSuccess: recarregar,
  });

  const remover = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/tarefas/${tarefaId}/anexos/${id}`, { method: 'DELETE' }),
    onSuccess: recarregar,
  });

  /**
   * Sobe os arquivos escolhidos, um a um.
   *
   * <p>O erro aparece, com o nome do arquivo. É a mesma lição do anexo de comentário, que
   * engolia a falha num {@code catch} vazio e produzia o "está enviando e sumindo": um
   * arquivo que falha não impede os outros, e o motivo tem de chegar em quem mandou.
   */
  async function anexar(arquivos: Iterable<File> | null) {
    const lista = arquivos ? Array.from(arquivos) : [];
    if (!lista.length) return;
    setErro(null);
    const falhas: string[] = [];

    for (const arquivo of lista) {
      setSubindo((n) => n + 1);
      try {
        const salvo = await subirAnexo(arquivo);
        await prender.mutateAsync({
          url: salvo.url,
          miniatura: salvo.miniatura ?? null,
          nomeOriginal: salvo.nomeOriginal,
          bytes: salvo.bytes,
        });
      } catch (e) {
        falhas.push(`${arquivo.name}: ${e instanceof Error ? e.message : 'falhou'}`);
      } finally {
        setSubindo((n) => n - 1);
      }
    }

    if (falhas.length) setErro(falhas.join(' · '));
    if (campo.current) campo.current.value = '';
  }

  /**
   * Cola uma imagem da área de transferência direto nos anexos.
   *
   * <p>Ouve a janela inteira, não só esta seção: a tarefa está aberta, e não importa se o
   * foco está na descrição ou num comentário quando a pessoa aperta Ctrl+V com um print
   * copiado — o material da tarefa é aqui. Só entra em ação se a área de transferência
   * tiver imagem; colar texto num campo continua colando texto, sem interferência.
   */
  useEffect(() => {
    function aoColar(e: ClipboardEvent) {
      const itens = e.clipboardData?.items;
      if (!itens) return;
      const imagens = Array.from(itens)
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((arquivo): arquivo is File => arquivo !== null);

      if (!imagens.length) return;
      e.preventDefault();
      void anexar(imagens);
    }

    window.addEventListener('paste', aoColar);
    return () => window.removeEventListener('paste', aoColar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefaId]);

  const lista = data ?? [];

  return (
    <div className="ax">
      {isPending && <div className="ax-vazio">carregando os anexos…</div>}

      {!isPending && lista.length === 0 && (
        <div className="ax-vazio">
          <b>Nenhum arquivo ainda</b>
          O que é material da tarefa mora aqui — contrato, print, planilha. O que é
          discussão vai nos comentários.
        </div>
      )}

      {lista.length > 0 && (
        <div className="ax-lista">
          {lista.map((a) => (
            <div key={a.id} className={`ax-item${a.miniatura ? ' img' : ''}`}>
              {/* Botão com fetch autenticado, e não <a target=_blank>: a aba nova não
                  manda o token, e o servidor devolvia o 401 no lugar da imagem. */}
              <button
                type="button"
                className="ax-abrir"
                onClick={() => void abrirAnexo(a.url, a.nomeOriginal)}
                title={`${a.nomeOriginal} · ${tamanho(a.bytes)}`}
              >
                {a.miniatura ? (
                  <ImagemAutenticada url={a.miniatura} alt={a.nomeOriginal} />
                ) : (
                  <span className="ax-ext">
                    {(a.nomeOriginal.split('.').pop() ?? '?').slice(0, 4).toUpperCase()}
                  </span>
                )}
                <span className="ax-txt">
                  <span className="ax-nome">{a.nomeOriginal}</span>
                  <span className="ax-meta">
                    {tamanho(a.bytes)}
                    {a.anexadoPor && ` · ${a.anexadoPor}`}
                  </span>
                </span>
              </button>
              <button
                className="ax-tirar"
                title="Tirar da tarefa"
                aria-label={`Tirar ${a.nomeOriginal}`}
                onClick={() => {
                  if (confirm(`Tirar "${a.nomeOriginal}" desta tarefa?`)) remover.mutate(a.id);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {erro && (
        <div className="ax-erro" role="alert">
          {erro}
          <button onClick={() => setErro(null)} aria-label="Fechar">
            ×
          </button>
        </div>
      )}

      <div className="ax-rodape">
        <button className="ax-add" onClick={() => campo.current?.click()}>
          📎 Anexar arquivo
        </button>
        {subindo > 0 && <span className="ax-subindo">enviando {subindo}…</span>}
        <span className="ax-dica">
          imagem, PDF, doc, planilha, HTML, zip, mídia — até 10 MB cada · ou cole (Ctrl+V) uma
          imagem copiada
        </span>
        <input
          ref={campo}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void anexar(e.target.files)}
        />
      </div>
    </div>
  );
}
